import { Card, Col, Form, Input, InputNumber, Layout, Row, Switch } from "antd";
import { toNumber } from "lodash";
import includes from "lodash/includes";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FormActionButton from "src/components/common/FormActionButton";
import { messages } from "src/constants/messages";
import { apiCaller, cancelListToken } from "src/util/apiCaller";
import { MessageStatusError } from "src/util/MessageStatus";

const FormItem = Form.Item;
const initialState = {
  tenNhomTuLieu: "",
  thuTu: 0
};

const NhomTaiLieuForm = ({ status }: any) => {
  const { isActive } = status;
  const navigate = useNavigate();
  const location = useLocation();
  const [type, setType] = useState("new");
  const [fieldTouch, setFieldTouch] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingButtonClose, setLoadingButtonClose] = useState(false);
  const [form] = Form.useForm();
  const { tenNhomTuLieu, thuTu } = initialState;
  const { validateFields, resetFields, setFieldsValue } = form;
  const { id } = useParams();

  useEffect(() => {
    if (isActive) {
      const load = () => {
        if (includes(location.pathname, "them-moi")) {
          setType("new");
        } else {
          setType("edit");
          getInfo();
        }
      };
      load();
    } else {
      navigate("/error/401");
    }
  }, []);

  useEffect(() => {
    return () => {
      cancelListToken(["infonhomtailieu"]);
    };
  }, []);

  /**
   * getInfo : Lấy thông tin
   * @memberof  NhomTaiLieuForm
   */
  const getInfo = async () => {
    if (location.state && location.state.itemData) {
      // Nếu có dữ liệu truyền sang
      const res = location.state.itemData;
      setFieldsValue({
        nhomtailieu: res,
      });
    } else {
      const configAPI = {
        url: `api/nhomtailieu/${id}`,
        method: "GET",
        value: "",
        apiType: "DETAIL",
        getName: "infonhomtailieu",
      };
      const response = await apiCaller(configAPI);
      try {
        if (
          response?.data &&
          (response.status === 200 || response.status === 201)
        ) {
          setFieldsValue({
            nhomtailieu: response.data,
          });
        } else {
          MessageStatusError(toNumber(response?.status));
        }
      } catch (error) {
        // Xử lý lỗi khi lưu dữ liệu không thành công
      }
    }
  };

  /**
   * goBack : quay lại trang danh sách
   * @memberof  NhomTaiLieuForm
   */
  const goBack = () => {
    navigate("/quan-tri/nhom-tu-lieu");
  };

  /**
   * save: Lưu
   * @memberof NhomTaiLieuForm
   */
  const save = () => {
      validateFields()
        .then((values) => {
          setLoadingButton(true);
          saveData(values, false);
        })
        .catch(() => {
          setLoadingButton(false);
        });
  };

  /**
   * saveAndClose: Lưu và đóng
   * @memberof NhomTaiLieuForm
   */
  const saveAndClose = () => {
      validateFields()
        .then((values) => {
          setLoadingButton(true);
          saveData(values, true);
        })
        .catch(() => {
          setLoadingButton(false);
        });
  };

  /**
   * saveData: Lưu dữ liệu
   * @param {object} values
   * @param {boolean} saveQuit
   * @memberof NhomTaiLieuForm
   */
  const saveData = async (values: any, saveQuit = false) => {
    try {
      const data = values.nhomtailieu;
      data.isSuDung = true;
      let configAPI;
      if (type === "new") {
        configAPI = {
          url: `api/nhomtulieu/create`,
          method: "POST",
          data: data,
          apiType: "ADD",
          isShowMessage: true,
        };
      } else {
        configAPI = {
          url: `api/nhomtulieu/${id}`,
          method: "PUT",
          data: data,
          apiType: "EDIT",
          isShowMessage: true,
        };
      }

      const response = await apiCaller(configAPI);
      if (response?.status === 200 || response?.status === 201) {
        if (saveQuit) {
          goBack();
          setLoadingButtonClose(false);
        } else {
          if (type === "new") {
            resetFields();
          }
          setFieldTouch(false);
          setLoadingButton(false);
        }
      } else {
        MessageStatusError(toNumber(response?.status));
        setLoadingButton(false);
      }
    } catch (error) {
      setLoadingButton(false);
    }
  };

  const formTitle = type === "new" ? "Thêm mới nhóm tài liệu" : "Chỉnh sửa nhóm tài liệu";
  return (
    <Layout>
      <div style={{ display: "flex" }}>
        <Col md={12}>
          <h2 style={{ color: "black", fontSize: 20, marginTop: 10 }}>
            {formTitle}
          </h2>
        </Col>
      </div>
      <Card>
        <Form
          className="form-container wrapper-content-inner"
          layout="vertical"
          form={form}
          name="nhomtailieu-control"
          onFieldsChange={() => setFieldTouch(true)}
        >
          <Row gutter={24}>
            <Col md={12} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Tên nhóm tài liệu"
                name={["nhomtailieu", "tenNhomTuLieu"]}
                rules={[
                  {
                    type: "string",
                    required: true,
                    message: messages.ERROR_REQUIRED,
                  },
                ]}
                initialValue={tenNhomTuLieu}
              >
                <Input className="input-item" placeholder="Nhập tên nhóm tài liệu" />
              </FormItem>
            </Col>
            <Col md={12} xs={24}>
              <FormItem
                className="ant-input-group"
                label="Thứ tự"
                name={["nhomtailieu", "thuTu"]}
                rules={[
                  {
                    required: true,
                    message: messages.ERROR_REQUIRED,
                  },
                ]}
                initialValue={thuTu}
              >
                <InputNumber min={0} className="input-item" placeholder="Nhập thứ tự" />
              </FormItem>
            </Col>
          </Row>
          <hr/>
          <FormActionButton
            goBack={goBack}
            saveAndClose={saveAndClose}
            disabled={fieldTouch}
            save={save}
            type={type}
            loadingButton={loadingButton}
            loadingButtonClose={loadingButtonClose}
          />
        </Form>
      </Card>
    </Layout>
  );
};

export default NhomTaiLieuForm;
